import React, { useState ,useEffect ,useRef } from 'react'

//-- components
import { useStateProps ,useImageProps ,useFormatProps } from '../ImpaasComponents.js';
import ImageLoader from '../ImageLoader.tsx';
import ImageSaver from '../ImageSaver.tsx';
import FormatSelect from '../FormatSelect.tsx';

//-- ui
import type { SliderSingleProps ,InputNumberProps } from 'antd';
import { Collapse, Select ,InputNumber ,Slider, ColorPicker ,Alert } from "antd";

//-- css
import './TransformApp.css';

//! ***
const { round ,min ,max ,abs ,sign } = Math;

//! *** Main Component ***
export default function TransformApp(props,ref) {
    const imgRef = useRef(null);

//-- ImPaaS component
    const { hasStateChanges ,onStateChanges ,isQueuing ,adviseDownload ,adviseQueuing } = useStateProps(props);
    const { noImage ,hasImage ,imageUrl ,imageInfoHeader ,imageWidth ,imageHeight ,onImageUpload } = useImageProps(props);
    const { formatName ,formatSelect } = useFormatProps(props);

//-- IMAGE
    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        onStateChanges(false);
    }

//-- PARAMETERS

    //-- scale
    const [scale, setScale] = useState(1) //! factor, nb could use (-1 ,-1)
    const [scaleX, setScaleX] = useState(1) //! negative used for mirror H
    const [scaleY, setScaleY] = useState(1) //! negative uses for mirror V
    const [scaleMethod, setScaleMethod] = useState('Linear');

    const scaleFormatter: NonNullable<SliderSingleProps['tooltip']>['formatter'] = (value) => {
        if( value === undefined ) {
            return 'Scale 100%'; // default
        }

        const roundedValue = round(value * 100);
        return `Scale ${roundedValue}%`; // Use the rounded value in the formatted string
    };

    function formatScalePercent(value) {
        //! scale is a factor and may be negative, showing positive value as percent
        const roundedValue = round(abs(value) * 100);
        return `${roundedValue}%`; 
    }

    function parseScalePercent(value) {
        const cleanedValue = value?.replace('%', '');
        return cleanedValue ? parseFloat(cleanedValue) / 100 : undefined;
    }

    const onChangeScale: InputNumberProps['onChange'] = (value) => {
        setScale(value);
        onStateChanges(true);
    };

    const onChangeScaleX: InputNumberProps['onChange'] = (value) => {
        setScaleX( sign(scaleX) * value );
        onStateChanges(true);
    };

    const onChangeScaleY: InputNumberProps['onChange'] = (value) => {
        setScaleY( sign(scaleY) * value );
        onStateChanges(true);
    };

    //-- Rotate
    const [rotate, setRotate] = useState(0)
    const [rotateBackground, setRotateBackground] = useState('#000000');

    const angleFormatter: NonNullable<SliderSingleProps['tooltip']>['formatter'] = (value) => `Rotate ${value}°`;

    function formatAngle(value) {
        const roundedValue = round(value);
        return `${roundedValue}°`; // Use the rounded value in the formatted string
    }

    function parseAngle(value) {
        const cleanedValue = value?.replace('°', '');
        return cleanedValue ? parseFloat(cleanedValue) : undefined;
    }

    const onChangeRotation: InputNumberProps['onChange'] = (value) => {
        setRotate(min(180, max(-180, Number(value))))
        onStateChanges(true);
    };

    //-- mirror
    const [mirrorDirection, setMirrorDirection] = useState('none');

    const onMirrorChange = (value: string) => {
        setMirrorDirection(value)

        switch( value ) {
        case 'none':
            setScaleX( abs(scaleX) )
            setScaleY( abs(scaleY) )
            break;
        case 'horizontal':
            setScaleX( -abs(scaleX) )
            setScaleY( abs(scaleY) )
            break;
        case 'vertical':
            setScaleX( abs(scaleX) )
            setScaleY( -abs(scaleY) )
            break;
        case 'both':
            setScaleX( -abs(scaleX) )
            setScaleY( -abs(scaleY) )
            break;
        };

        onStateChanges(true);
    }

//-- EFFECTs
    useEffect(() => {
        if( noImage ) return;

        const imageCanvas = document.getElementById('imageCanvasID');
        const imageWrapper = document.getElementById('imageWrapperID');
        if( !imageCanvas || !imageWrapper ) return; //! should not happen

        const canvas = imageCanvas.getBoundingClientRect();
        const rect = imgRef.current.getBoundingClientRect();

        imageWrapper.style.top = (canvas.height - rect.height - 30) / 2 + 'px'; //? WHY -30 !?
        imageWrapper.style.left = (canvas.width - rect.width) / 2 + 'px';

        imageWrapper.style.width = rect.width + 'px';
        imageWrapper.style.height = rect.height + 'px';

        onStateChanges(true);

    }, [scale,scaleX,scaleY,rotate]);

//-- COMPONENT
    const { Panel } = Collapse;
    const { Option } = Select;

    return (
      <div className="CropApp">
        <div class="upload-container">
          {!!imageUrl && (
            <>
            <div style={{textAlign: 'left' }}>
              <Alert message={imageInfoHeader} type="info" showIcon />
            </div>          
            <div class="canvas-container">
              <div id="imageCanvasID" class="transform-canvas">
              <div id="imageWrapperID" class="image-wrapper" style={{ backgroundColor: rotateBackground }} />
              <img
                  ref={imgRef}
                  alt="Transform"
                  src={imageUrl}
                  style={{ transform: `scale(${scale}) rotate(${rotate}deg) scaleX(${scaleX}) scaleY(${scaleY})` }}
                  onLoad={onImageLoad}
              />
              </div>
              <div class="transform-scale">
                <Slider min={0.1} max={4} step={0.01} vertical tooltip={{ formatter:scaleFormatter ,placement: "right"}} value={scale} onChange={setScale} />
              </div>
              <div class="transform-rotate">
                <Slider min={-180} max={180} tooltip={{ formatter:angleFormatter ,placement: "bottom"}} value={rotate} onChange={setRotate} />
              </div>
              <div class="transform-caret"/>
            </div>
            </>
          )}
          <hr/>
          <div class="container-fluid d-flex justify-content-between align-items-center">
              <div class="container-fluid d-flex justify-content-center">
              {!isQueuing && (
                  <ImageLoader onUpload={onImageUpload}/>
                )}
                {!isQueuing && hasImage && (
                  <button disabled={!hasStateChanges} type="button" class="process-button"> Apply </button>
                )}
                <ImageSaver enabled={hasImage} queueTime="5" onDownload={adviseDownload} onQueue={adviseQueuing}/>
              </div>            
          </div>
        </div>
        
        <div class="method-group">
            <ul class="property-list">
              <Collapse defaultActiveKey={['1','2','3','4','5']}>
                <Panel header={<h6>Scale</h6>} key="1" className="property-panel">
                  <InputNumber<number>
                      name="scale_factor" min={0.1} max={100} step={0.1} style={{ width: 180 }}
                      disabled={noImage} value={scale} onChange={onChangeScale}
                      formatter={(value) => formatScalePercent(value)}
                      parser={(value) => parseScalePercent(value)}
                    />

                  <label class="padded-left">Width:</label>
                  <InputNumber<number>
                    name="scale_width" min={0.1} max={100} step={0.1}
                    disabled={noImage} value={Math.abs(scaleX)} onChange={onChangeScaleX}
                    formatter={(value) => formatScalePercent(value)}
                    parser={(value) => parseScalePercent(value)}
                  />
                  <label class="padded-left">Height:</label>
                  <InputNumber<number>
                    name="scale_height" min={0.1} max={100} step={0.1}
                    disabled={noImage} value={Math.abs(scaleY)} onChange={onChangeScaleY}
                    formatter={(value) => formatScalePercent(value)}
                    parser={(value) => parseScalePercent(value)}
                  />

                  <label class="padded-left">Unit:</label> 
                  <Select disabled={noImage} name="scale_unit" value="Percent" style={{ width: 100 }}>
                    <Option value="Pixels">Pixels</Option>
                    <Option value="Percent">Percent</Option>
                  </Select>

                  <label class="padded-left">Method:</label>
                  <input type="hidden" name="scale_method" value={scaleMethod} /> 
                  <Select disabled={noImage} defaultValue="Linear" style={{ width: 120 }} onChange={(value) => setScaleMethod(value)}>
                    <Option value="nearest">Nearest</Option>
                    <Option value="linear">Linear</Option>
                    <Option value="area">Area</Option>
                    <Option value="cubic">Cubic</Option>
                    <Option value="lanczos">Lanczos</Option>
                    <Option value="reshape">Reshape</Option>
                  </Select>
                </Panel>
                <Panel header={<h6>Rotate</h6>} key="2" className="property-panel">
                  <InputNumber<number>
                    name="rotate_angle" min={-180} max={180} step={1} style={{ width: 180 }}
                    disabled={noImage} value={rotate} onChange={onChangeRotation}
                    formatter={(value) => formatAngle(value)}
                    parser={(value) => parseAngle(value)}
                  />

                  <label>Background:</label>
                  <input type="hidden" name="rotate_background" value={rotateBackground} /> 
                  <ColorPicker disabled={noImage} defaultValue="black" showText disabledAlpha onChange={(value) => setRotateBackground(value.toHexString())} />
                </Panel>
                <Panel header={<h6>Mirror</h6>} key="3" className="property-panel">
                  <input type="hidden" name="mirror_direction" value={mirrorDirection} />
                  <Select defaultValue="none" style={{ width: 180 }}
                    disabled={noImage}
                    onChange={onMirrorChange}
                  >
                    <Option value="none">None</Option>
                    <Option value="horizontal">Horizontal</Option>
                    <Option value="vertical">Vertical</Option>
                    <Option value="both">Both</Option>
                  </Select>
                </Panel>
                <Panel header={<h6>Format</h6>} key="4" className="property-panel">
                  <FormatSelect enabled={hasImage} name={formatName} onSelect={formatSelect} />
                </Panel>
              </Collapse>         
            </ul>      
        </div>
      </div>
    )
}
